import { ScopeReportLineItem } from '@kaiizen/kai-reports';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

export const useScrollAndHighlight = (scopeLineItems: ScopeReportLineItem[]) => {
  const SCROLL_THRESHOLD = 100;
  const [searchHighlightedTaskId, setSearchHighlightedTaskId] = useState<string | undefined>(undefined);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { groupId, taskId } = useParams();
  const [searchParams] = useSearchParams();
  const timestamp = searchParams.get('t');

  const handleScrollToTop = () => {
    scrollContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setShowScrollToTop(scrollContainer.scrollTop > SCROLL_THRESHOLD);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current && scopeLineItems) {
      if (taskId && groupId) {
        const taskElement = document.getElementById(`task-${taskId}`);
        if (taskElement) {
          setTimeout(() => {
            taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }

        setSearchHighlightedTaskId(taskId);
        if (searchHighlightTimeoutRef.current) {
          clearTimeout(searchHighlightTimeoutRef.current);
        }
        searchHighlightTimeoutRef.current = setTimeout(() => {
          setSearchHighlightedTaskId(undefined);
        }, 2000);
      } else if (groupId) {
        const groupElement = document.getElementById(`group-${groupId}`);
        if (groupElement) {
          setTimeout(() => {
            groupElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    }
  }, [groupId, taskId, scopeLineItems, timestamp]);

  useEffect(() => {
    return () => {
      if (searchHighlightTimeoutRef.current) {
        clearTimeout(searchHighlightTimeoutRef.current);
      }
    };
  }, []);

  return {
    scrollContainerRef,
    searchHighlightedTaskId,
    handleScrollToTop,
    showScrollToTop,
  };
};
